// The two drawers shared by Home, Students and Reports: Edit and Details. One copy, so
// a change to what a counsellor sees on a student is made once.
import { useState } from 'react';

import { Drawer, Fact, FactGrid, GroupTitle, Notice, StatusChip } from 'src/front-components/lead-desk/kit';
import { StudentFormFields } from 'src/front-components/lead-desk/student-form';
import type { DeskContext } from 'src/front-components/lead-desk/context';
import { DeskButton, ErrorLine } from 'src/front-components/theme/desk-controls';
import { getShellColors, SHELL_RADIUS, SHELL_SPACE, SHELL_TYPE, v } from 'src/front-components/theme/shell-tokens';
import { fetchCallHistory, updateStudent } from 'src/front-components/utils/lead-desk-data';
import { useLiveData } from 'src/front-components/utils/desk-data';
import {
  DESK_STATUS_COLORS,
  DESK_STATUS_LABELS,
  formatCallDate,
  formFromStudent,
  initials,
  isClosed,
  SOURCE_LABELS,
  studentPhone,
  studentUpdateFromForm,
  UNASSIGNED,
  validateStudentForm,
  type FormErrors,
  type StudentForm,
  type StudentRow,
} from 'src/utils/lead-desk';

export const EditDrawer = ({ ctx, student, onClose }: { ctx: DeskContext; student: StudentRow; onClose: () => void }) => {
  const [form, setForm] = useState<StudentForm>(formFromStudent(student));
  const [errors, setErrors] = useState<FormErrors>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    const found = validateStudentForm(form);

    setErrors(found);

    if (Object.keys(found).length > 0) return;

    setBusy(true);
    setFailure(null);

    try {
      await updateStudent(student.id, studentUpdateFromForm(form));
      await ctx.refresh();
      onClose();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      scheme={ctx.scheme}
      title={`Edit ${student.name ?? 'student'}`}
      subtitle={studentPhone(student)}
      avatar={initials(student.name)}
      onClose={onClose}
      footer={
        <>
          <DeskButton scheme={ctx.scheme} primary disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </DeskButton>
          <DeskButton scheme={ctx.scheme} onClick={onClose}>
            Cancel
          </DeskButton>
          <ErrorLine scheme={ctx.scheme} message={failure} />
        </>
      }
    >
      <StudentFormFields
        scheme={ctx.scheme}
        form={form}
        errors={errors}
        onChange={(patch) => setForm({ ...form, ...patch })}
        profile={ctx.profile}
        counsellors={ctx.counsellors}
        people={ctx.people}
        fixedCounsellor={ctx.role === 'COUNSELLOR' ? ctx.person : ctx.role === 'TELECALLER' ? (student.counsellorName ?? '') : undefined}
        fixedTelecaller={ctx.role === 'TELECALLER' ? (student.telecallerName ?? ctx.person) : undefined}
      />
    </Drawer>
  );
};

export const DetailsDrawer = ({ ctx, student, onClose }: { ctx: DeskContext; student: StudentRow; onClose: () => void }) => {
  const shell = getShellColors(ctx.scheme);
  const [tab, setTab] = useState<'PROFILE' | 'CALLS'>('PROFILE');
  const calls = useLiveData(() => fetchCallHistory(student.id), 30_000);
  const closed = isClosed(student);
  const statusLabel = DESK_STATUS_LABELS[student.deskStatus ?? ''] ?? '—';

  return (
    <Drawer
      scheme={ctx.scheme}
      title={student.name ?? 'Student'}
      subtitle={`ID: ${student.id.slice(0, 8)}`}
      avatar={initials(student.name)}
      onClose={onClose}
      tabs={[
        { id: 'PROFILE', label: 'Student Profile' },
        { id: 'CALLS', label: 'Call History' },
      ]}
      active={tab}
      onTab={(id) => setTab(id === 'CALLS' ? 'CALLS' : 'PROFILE')}
    >
      {tab === 'PROFILE' ? (
        <>
          {closed && <Notice tone="red">Lead Closed – No further follow-ups allowed.</Notice>}
          {student.isBlocked === true && <Notice tone="orange">This student is blocked and is not in any call queue.</Notice>}

          <div style={{ border: `1px solid ${shell.borderMedium}`, borderRadius: SHELL_RADIUS.md, padding: SHELL_SPACE[3] }}>
            <div style={{ ...SHELL_TYPE.label, color: shell.text, marginBottom: SHELL_SPACE[2] }}>LEAD CONTEXT</div>
            <FactGrid>
              <Fact scheme={ctx.scheme} label="Counselor" value={student.counsellorName ?? UNASSIGNED} />
              <Fact scheme={ctx.scheme} label="Telecaller" value={student.telecallerName ?? UNASSIGNED} />
              <Fact scheme={ctx.scheme} label="Acquisition Source" value={SOURCE_LABELS[student.leadSource ?? ''] ?? student.leadSource} />
              <Fact scheme={ctx.scheme} label="Status" value={<StatusChip label={statusLabel} color={DESK_STATUS_COLORS[student.deskStatus ?? ''] ?? 'gray'} />} />
              <Fact scheme={ctx.scheme} label="Next Call Date" value={student.nextCallAt ? formatCallDate(student.nextCallAt) : 'Not Scheduled'} />
            </FactGrid>
          </div>

          <GroupTitle scheme={ctx.scheme}>Student Details</GroupTitle>
          <FactGrid>
            <Fact scheme={ctx.scheme} label="Student Name" value={student.name} />
            <Fact scheme={ctx.scheme} label="Email Address" value={student.email?.primaryEmail ?? 'Not provided'} />
            <Fact scheme={ctx.scheme} label="Mobile Number" value={studentPhone(student)} />
            <Fact scheme={ctx.scheme} label="Alternate Phone" value={student.alternatePhone ?? 'None'} />
          </FactGrid>

          <GroupTitle scheme={ctx.scheme}>Parental &amp; Location Information</GroupTitle>
          <FactGrid>
            <Fact scheme={ctx.scheme} label="Parent/Father Name" value={student.parentName ?? 'Not provided'} />
            <Fact scheme={ctx.scheme} label="Parent Contact" value={student.parentPhone ?? 'Not provided'} />
            <Fact scheme={ctx.scheme} label="Parent Occupation" value={student.parentOccupation ?? 'Not provided'} />
            <Fact scheme={ctx.scheme} label="City Location" value={student.city} />
          </FactGrid>
          <div style={{ marginTop: SHELL_SPACE[3] }}>
            <FactGrid>
              <Fact scheme={ctx.scheme} label="District" value={student.district} />
              <Fact scheme={ctx.scheme} label="State" value={student.state} />
              <Fact scheme={ctx.scheme} label="Pincode" value={student.pincode ?? '—'} />
              <Fact scheme={ctx.scheme} label="Address" value={student.streetAddress ?? '—'} />
            </FactGrid>
          </div>

          <GroupTitle scheme={ctx.scheme}>Abroad & Academic Details</GroupTitle>
          <FactGrid>
            <Fact scheme={ctx.scheme} label="Course Interest" value={student.courseInterest ?? 'MBBS'} />
            <Fact scheme={ctx.scheme} label="Preferred Country" value={student.preferredCountry ?? student.countryInterest ?? 'Not specified'} />
            <Fact scheme={ctx.scheme} label="Target University" value={student.preferredUniversity ?? 'Open to recommendation'} />
            <Fact scheme={ctx.scheme} label="Budget Range" value={student.budget ?? 'Not specified'} />
          </FactGrid>
          <div style={{ marginTop: SHELL_SPACE[3] }}>
            <FactGrid>
              <Fact scheme={ctx.scheme} label="NEET Score" value={student.neetScore ?? 'Not provided'} />
              <Fact scheme={ctx.scheme} label="NEET Qualified" value={student.neetQualified ?? 'Awaiting'} />
              <Fact scheme={ctx.scheme} label="NEET Exam Year" value={student.neetYear ?? '—'} />
              <Fact scheme={ctx.scheme} label="12th PCB %" value={student.twelfthPercentage ?? '—'} />
            </FactGrid>
          </div>
          <div style={{ marginTop: SHELL_SPACE[3] }}>
            <FactGrid>
              <Fact scheme={ctx.scheme} label="10th Board %" value={student.tenthPercentage ?? '—'} />
              <Fact scheme={ctx.scheme} label="Intake / Batch" value={student.intakeYear ?? '2026'} />
              <Fact scheme={ctx.scheme} label="Passport Status" value={student.passportStatus ?? 'Not specified'} />
              <Fact scheme={ctx.scheme} label="Lead Priority" value={student.priority ?? 'MEDIUM'} />
            </FactGrid>
          </div>
          <div style={{ marginTop: SHELL_SPACE[3] }}>
            <FactGrid>
              <Fact scheme={ctx.scheme} label="Gender" value={student.gender ?? '—'} />
              <Fact scheme={ctx.scheme} label="Date of Birth" value={student.dateOfBirth ?? '—'} />
              <Fact scheme={ctx.scheme} label="Category / Quota" value={student.category ?? 'General'} />
              <Fact scheme={ctx.scheme} label="Preferred Language" value={student.preferredLanguage ?? '—'} />
            </FactGrid>
          </div>

          {(student.notes ?? '') !== '' && (
            <>
              <GroupTitle scheme={ctx.scheme}>Comments</GroupTitle>
              <div style={{ ...SHELL_TYPE.body, color: shell.text, whiteSpace: 'pre-wrap', background: shell.backgroundSecondary, padding: SHELL_SPACE[3], borderRadius: SHELL_RADIUS.sm }}>
                {student.notes}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div style={{ ...SHELL_TYPE.sectionTitle, color: shell.text, marginBottom: SHELL_SPACE[3] }}>Logged Call History</div>
          <ErrorLine scheme={ctx.scheme} message={calls.error} />
          {calls.loading && <div style={{ ...SHELL_TYPE.body, color: shell.textTertiary }}>Loading…</div>}
          {calls.data !== null && calls.data.length === 0 && (
            <div style={{ ...SHELL_TYPE.body, color: shell.textTertiary }}>No calls have been logged for this student yet.</div>
          )}
          {(calls.data ?? []).map((call) => (
            <div key={call.id} style={{ borderLeft: `2px solid ${v('color-blue')}`, paddingLeft: SHELL_SPACE[3], marginBottom: SHELL_SPACE[4] }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', ...SHELL_TYPE.bodyMedium, color: shell.text }}>
                <span>{call.name ?? 'Call logged'}</span>
                <span style={{ ...SHELL_TYPE.caption, color: shell.textTertiary }}>{formatCallDate(call.sessionDate)}</span>
              </div>
              <div style={{ ...SHELL_TYPE.caption, color: shell.textSecondary }}>Counsellor: {call.counsellor ?? '—'}</div>
              {(call.summary ?? '') !== '' && (
                <div style={{ ...SHELL_TYPE.body, color: shell.text, background: shell.backgroundSecondary, padding: SHELL_SPACE[2], borderRadius: SHELL_RADIUS.sm, marginTop: SHELL_SPACE[1] }}>
                  {call.summary}
                </div>
              )}
              {call.status !== null && call.status !== undefined && <div style={{ marginTop: SHELL_SPACE[1] }}><StatusChip label={`Status: ${call.status}`} color="blue" /></div>}
            </div>
          ))}
        </>
      )}
    </Drawer>
  );
};
